import * as Sentry from '@sentry/react'
import { logger } from './logger'

const log = logger('Error')

interface ErrorContext {
  /** Where the error occurred (e.g. 'sync', 'auth', 'editor') */
  source: string
  /** Additional metadata to attach to the Sentry event */
  extra?: Record<string, unknown>
}

/**
 * Capture and report an error to both the logger and Sentry.
 *
 * Use this instead of bare `log.error()` or `console.error()` so that
 * every error is consistently logged locally AND sent to Sentry with
 * structured context.
 *
 * @example
 *   captureError(error, { source: 'sync', extra: { noteId } })
 */
export function captureError(error: unknown, context: ErrorContext): void {
  const message = error instanceof Error ? error.message : String(error)

  // Always log locally
  log.error(`[${context.source}] ${message}`, error)

  // Send to Sentry with structured context
  Sentry.withScope((scope) => {
    scope.setTag('error_source', context.source)
    if (context.extra) {
      for (const [key, value] of Object.entries(context.extra)) {
        scope.setExtra(key, value)
      }
    }

    if (error instanceof Error) {
      Sentry.captureException(error)
    } else {
      Sentry.captureMessage(message, 'error')
    }
  })
}
